import { useEffect, useState } from 'react';
import { formatCompetencia, formatPct, mesesDisponiveis } from '@/lib/format';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { Button } from './ui';

export function MonthPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const meses = mesesDisponiveis();
  return (
    <div className="field month-picker">
      <label htmlFor="mes">Mês</label>
      <select id="mes" value={value} onChange={(e) => onChange(e.target.value)}>
        {meses.map((m) => (
          <option key={m} value={m}>
            {formatCompetencia(m)}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Barra de progresso da meta. Escala de 0 a 100%; marcador no piso do bônus.
 * Acima de 100% a barra fica cheia e o rótulo mostra o percentual real.
 */
export function GoalBar({ atingimento, piso = 0.7, label }: { atingimento: string | number; piso?: number; label: string }) {
  const frac = Number(atingimento);
  const width = Math.min(100, Math.max(0, frac * 100));
  const tone = frac >= 1 ? 'goal--ok' : frac >= piso ? 'goal--mid' : 'goal--low';
  // Marcador só quando o piso fica no meio da barra (piso de 100% coincide com o fim: sem rótulo duplicado).
  const marcador = piso > 0 && piso < 1;
  return (
    <div className={`goal ${tone}`}>
      <div className="goal__track" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(frac * 100)}>
        <div className="goal__fill" style={{ width: `${width}%` }} />
        {marcador && <div className="goal__marker" style={{ left: `${piso * 100}%` }} aria-hidden="true" />}
      </div>
      <div className="goal__scale" aria-hidden="true">
        {marcador && (
          <span style={{ left: `${piso * 100}%` }} className="goal__scale-mark">
            {formatPct(piso)}
          </span>
        )}
        <span className="goal__scale-end">Meta</span>
      </div>
    </div>
  );
}

export function Kpi({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'warn' | 'ok' }) {
  return (
    <div className="kpi">
      <dt>{label}</dt>
      <dd className={tone ? `kpi--${tone}` : undefined}>{value}</dd>
      {hint && <p className="kpi__hint">{hint}</p>}
    </div>
  );
}

/** Botão "Atualizar agora": pede uma sincronização ao Conta Azul (espera de 30s entre cliques). */
export function RefreshButton({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  async function run() {
    setBusy(true);
    setMsg(null);
    try {
      await api.post('/integrations/conta-azul/sync');
      onDone();
      setCooldown(30);
    } catch (err) {
      setMsg(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="refresh">
      <Button variant="secondary" onClick={() => void run()} loading={busy} disabled={cooldown > 0}>
        {cooldown > 0 ? `Atualizar agora (${cooldown}s)` : 'Atualizar agora'}
      </Button>
      {msg && <span className="small refresh__msg">{msg}</span>}
    </div>
  );
}
